import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClubApi, type ClubCustomOrderSummary } from "../../services/clubApi";
import { devLog } from "../../utils/devLog";

const FILTER_OPTIONS = [
  { value: "all", label: "All Stories" },
  { value: "review_required", label: "Needs Review" },
  { value: "delivery_ready", label: "Ready" },
  { value: "delivered", label: "Delivered" },
] as const;

const formatDateTime = (value?: string | null) => {
  if (!value) return "Not yet";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not yet";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
};

const getStatusClasses = (status = "") => {
  switch (String(status || "").trim().toLowerCase()) {
    case "review_required":
      return "border-amber-300/30 bg-amber-300/10 text-amber-100";
    case "delivery_ready":
    case "delivered":
      return "border-emerald-300/30 bg-emerald-300/10 text-emerald-100";
    case "generation_failed":
      return "border-rose-300/30 bg-rose-300/10 text-rose-100";
    default:
      return "border-white/10 bg-white/5 text-[#d7d0c1]";
  }
};

const getPrimaryActionLabel = (entry: ClubCustomOrderSummary) => {
  const status = String(entry.status || "").trim().toLowerCase();
  if (status === "delivery_ready" || status === "delivered") return "Open Story";
  if (status === "review_required") return "Check Story Status";
  if (status === "generation_failed") return "Resume Build";
  return "Resume Intake";
};

const ClubCustomOrdersPage = () => {
  const [entries, setEntries] = useState<ClubCustomOrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<(typeof FILTER_OPTIONS)[number]["value"]>("all");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");
        devLog("route", "member_custom_orders_load_started", {
          route: "/agency/custom-cases",
          statusFilter,
          search,
        });

        const response = await ClubApi.getMyCustomOrders({
          status: statusFilter === "all" ? undefined : statusFilter,
          search: search.trim() || undefined,
          limit: 50,
        });

        if (cancelled) return;
        const nextEntries = Array.isArray(response?.entries) ? response.entries : [];
        setEntries(nextEntries);
        devLog("route", "member_custom_orders_load_succeeded", {
          route: "/agency/custom-cases",
          statusFilter,
          search,
          count: nextEntries.length,
        });
      } catch (err: any) {
        if (cancelled) return;
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load your custom stories.";
        setError(message);
        setEntries([]);
        devLog("route", "member_custom_orders_load_failed", {
          route: "/agency/custom-cases",
          statusFilter,
          search,
          status: err?.response?.status,
          message: String(message),
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [search, statusFilter]);

  return (
    <section className="mx-auto max-w-6xl px-4 pb-10 pt-4 text-[#eceae4]">
      <section className="mt-6 rounded-3xl border border-[#1d2230] bg-[radial-gradient(circle_at_85%_0%,rgba(101,79,42,0.18),transparent_40%),radial-gradient(circle_at_20%_0%,rgba(44,54,88,0.18),transparent_42%),#090d15] p-7">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Agency Custom Stories</p>
            <h1 className="mt-4 text-4xl font-black leading-[0.95] text-[#efede7] sm:text-5xl">
              Your Custom Stories
            </h1>
            <p className="mt-5 max-w-3xl text-base text-[#aba69a] sm:text-lg">
              Every custom story tied to your member email appears here. Use this page to resume intake, check build
              status, or reopen a finished story.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/custom"
              className="inline-flex items-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#ece1c8] hover:brightness-110"
            >
              Start Another Story
            </Link>
            <Link
              to="/agency/dashboard"
              className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#efe7d6] transition hover:bg-white/10"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-3xl border border-[#202632] bg-[#0a0f18] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {FILTER_OPTIONS.map((option) => {
              const active = option.value === statusFilter;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatusFilter(option.value)}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition ${
                    active
                      ? "border-[#6d6558] bg-[linear-gradient(180deg,rgba(58,55,47,0.82),rgba(19,23,31,0.9))] text-[#f4ecda]"
                      : "border-[#2d3442] bg-[#111722] text-[#aeb6c3] hover:border-[#465066] hover:text-[#e8e0cd]"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <label className="block w-full max-w-md">
            <span className="sr-only">Search your custom stories</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by city, title, or case id"
              className="w-full rounded-2xl border border-[#2b3241] bg-[#101520] px-4 py-3 text-sm text-[#efe7d6] placeholder:text-[#7f8693] focus:border-[#5b7fa3] focus:outline-none"
            />
          </label>
        </div>

        {loading ? (
          <div className="mt-6 rounded-2xl border border-[#272d38] bg-[#111622]/65 p-6 text-sm text-[#b6b1a3]">
            Loading your custom stories...
          </div>
        ) : error ? (
          <div className="mt-6 rounded-2xl border border-rose-300/25 bg-rose-950/30 p-6 text-sm text-rose-100">
            {error}
          </div>
        ) : entries.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-[#272d38] bg-[#111622]/65 p-6 text-sm text-[#b6b1a3]">
            No custom stories matched this view yet.
          </div>
        ) : (
          <div className="mt-6 grid gap-4">
            {entries.map((entry) => (
              <article
                key={entry.id}
                className="rounded-3xl border border-[#272d38] bg-[radial-gradient(circle_at_0%_0%,rgba(38,51,76,0.16),transparent_36%),#101520] p-5"
              >
                {entry.coverImageUrl ? (
                  <div className="relative mb-5 overflow-hidden rounded-2xl border border-white/10 bg-black/35">
                    <img
                      src={entry.coverImageUrl}
                      alt={`${entry.title} cover`}
                      className="aspect-[16/9] w-full object-cover saturate-[0.9] contrast-[1.05] brightness-[0.86]"
                      loading="lazy"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(3,8,18,0.08),rgba(3,8,18,0.32))]" />
                  </div>
                ) : null}
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${getStatusClasses(entry.status)}`}>
                        {entry.status.replace(/_/g, " ")}
                      </span>
                      <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[#b6b1a3]">
                        {entry.suspectCount} suspect{entry.suspectCount === 1 ? "" : "s"}
                      </span>
                      {entry.language ? (
                        <span className="rounded-full border border-[#3b475c] bg-[#111a28] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#c6d5ec]">
                          {entry.language}
                        </span>
                      ) : null}
                    </div>
                    <h2 className="mt-4 text-2xl font-semibold text-[#efede7]">{entry.title}</h2>
                    <p className="mt-2 text-sm text-[#aba69a]">{entry.lifecycleSummary}</p>
                    <dl className="mt-4 grid gap-3 text-sm text-[#d7d0c1] sm:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <dt className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">City</dt>
                        <dd className="mt-1">{entry.city || "Not set"}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Selected Idea</dt>
                        <dd className="mt-1">{entry.selectedIdeaTitle || "Not selected"}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Updated</dt>
                        <dd className="mt-1">{formatDateTime(entry.updatedAt)}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Language</dt>
                        <dd className="mt-1">{entry.language || "Not set"}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Delivery Email</dt>
                        <dd className="mt-1">{formatDateTime(entry.deliveryEmailSentAt)}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="flex shrink-0 flex-wrap gap-3">
                    <Link
                      to={entry.openIntakePath}
                      className="inline-flex items-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#ece1c8] hover:brightness-110"
                    >
                      {getPrimaryActionLabel(entry)}
                    </Link>
                    {entry.canOpenCase ? (
                      <Link
                        to={entry.openCasePath}
                        className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#efe7d6] transition hover:bg-white/10"
                      >
                        Open Case View
                      </Link>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </section>
  );
};

export default ClubCustomOrdersPage;
